import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PageList } from '../../components/PageComponents';
import Head from '../layouts/Head';
import LoadingDashboardContent from '../../components/LoadingDashboardContent';
import { API_URL } from '../../constants';

const AdminRoleList = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [pageData, setPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('name');
  const [searchValue, setSearchValue] = useState('');
  const [isReload, setIsReload] = useState(false);

  // Function to fetch role data
  const fetchRoles = async () => {
    setLoading(true);
    setProgress(0);
    
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'name';
    const searchValue = params.get('searchValue') || '';

    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    try {
      const response = await axios.get(`${API_URL}/roles`, {
        params: {
          page,
          limit,
          searchBy,
          searchValue,
        },
        onDownloadProgress: (progressEvent) => {
          setProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        },
      });
  
      const { data, dataCount } = response.data;
  
      setPageData({
        data,
        dataCount,
      });
  
      setLoading(false);
    } catch (error) {
      toast.error('Error fetching roles');
      console.error('Error fetching roles:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    
    if (isReload) {
      setIsReload(false);
    }
  }, [search, isReload]);

  const handlePageDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      const toastId = toast.loading('Deleting...', { autoClose: false });
      
      try {
        await axios.delete(`${API_URL}/roles/${id}`);
        
        setPageData((prev) => ({
          ...prev,
          data: prev.data.filter((role) => role.id !== id),
        }));

        toast.update(toastId, {
          render: 'Role deleted successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } catch (error) {
        toast.update(toastId, {
          render: 'Error deleting role!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        console.error('Error deleting role:', error);
      }
    }
  };

  // Update URL parameters
  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/user-role/list', search: params.toString() });
  };

  return (
    <>
      <Head title="Role List" />
      {loading ? (
        <LoadingDashboardContent progress={progress} />
      ) : (
        <PageList
          itemType="roles"
          itemId="id"
          pageTitle="Role List"
          pageData={pageData.data}
          actionApplyKeyMap={{
            Delete: 'delete',
          }}
          columns={['Name']}
          filterBy={{ ROLE: 'name', ID: 'id' }}
          columnKeyMap={{
            id: 'id',
            Name: 'name',
          }}          
          onAddNew={() => navigate('/admin/user-role/new')}
          onEdit={(id) => navigate(`/admin/user-role/edit/${id}`)}
          onDelete={handlePageDelete}
          totalRows={pageData.dataCount}
          updateUrlParams={updateUrlParams}
          isReload={setIsReload}
        />
      )}
    </>
  );
};

export default AdminRoleList;
